import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import SharedNavigation from "@/components/SharedNavigation";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const MagazineStorefront = () => {
  const [issues, setIssues] = useState([]);
  const [latestIssue, setLatestIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchIssues();
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
    } catch (error) {
      setCurrentUser(null);
    }
  };

  const fetchIssues = async () => {
    try {
      const response = await axios.get(`${API}/magazine/issues`);
      const allIssues = response.data;
      if (allIssues.length > 0) {
        setLatestIssue(allIssues[0]);
        setIssues(allIssues.slice(1));
      }
    } catch (error) {
      console.error("Failed to fetch issues:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyIssue = async (issueId) => {
    // Use logged-in user's email or prompt for one
    let email = currentUser?.email;
    if (!email) {
      email = prompt("Please enter your email to receive the magazine:");
      if (!email) return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/magazine/checkout`, {
        issue_id: issueId,
        user_email: email,
        origin_url: window.location.origin
      });

      if (response.data.has_access) {
        toast.success("You already own this issue!");
        navigate(`/magazine/purchase-success?issue_id=${issueId}&email=${email}`);
      } else if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Failed to start checkout", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={currentUser} activePage="magazine" />
        <div className="pt-24 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading magazine...</p>
        </div>
      </div>
    );
  }

  if (!latestIssue) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={currentUser} activePage="magazine" />
        <div className="pt-24 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">No Issues Available</h2>
          <p className="text-gray-400">Check back soon for our latest magazine!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08080c]">
      <SharedNavigation currentUser={currentUser} activePage="magazine" />

      {/* Hero Section - Latest Issue */}
      <section className="magazine-hero" data-testid="magazine-hero">
        <div className="container-custom py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Cover Image */}
            <div className="magazine-cover-container" data-testid="latest-cover">
              <img
                src={`${API}/magazine/public/cover/${latestIssue.cover_image}`}
                alt={latestIssue.title}
                className="magazine-cover-large"
              />
            </div>

            {/* Issue Details */}
            <div>
              <div className="mb-4">
                <span className="text-gold text-sm font-semibold uppercase tracking-wider">Latest Issue</span>
              </div>
              <h1 className="magazine-title mb-4" data-testid="latest-title">
                {latestIssue.title}
              </h1>
              <p className="text-gray-400 mb-2" data-testid="latest-date">
                {latestIssue.issue_date
                  ? new Date(latestIssue.issue_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long'
                    })
                  : ''}
              </p>
              <p className="text-gray-300 text-lg mb-6" data-testid="latest-description">
                {latestIssue.description || ''}
              </p>

              {/* Inside This Issue */}
              {Array.isArray(latestIssue.featured_artists) && latestIssue.featured_artists.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-white mb-4">Inside This Issue</h3>
                  <div className="space-y-2">
                    {latestIssue.featured_artists.map((artist, idx) => (
                      <div key={idx} className="flex items-center gap-3" data-testid={`featured-artist-${idx}`}>
                        <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-gold"></div>
                        <span className="text-gray-300">
                          <span className="font-semibold text-white">{artist?.name || 'Artist'}</span>
                          {artist?.category && (
                            <>
                              <span className="text-gray-500 mx-2">•</span>
                              <span className="text-purple-400">{artist.category}</span>
                            </>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Price and Actions */}
              <div className="flex items-center gap-4 mb-6">
                <div className="text-3xl font-bold text-gold">
                  ${(typeof latestIssue.price === 'number' ? latestIssue.price : parseFloat(latestIssue.price) || 0).toFixed(2)}
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={() => handleBuyIssue(latestIssue.issue_id)}
                  disabled={checkoutLoading}
                  className="buy-magazine-btn"
                  data-testid="buy-latest-btn"
                >
                  {checkoutLoading ? "Processing..." : "Buy This Issue"}
                </Button>
                <Link to="/login">
                  <Button variant="outline" className="submit-music-outline-btn">
                    Submit Your Music
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Preview Pages */}
      {Array.isArray(latestIssue.preview_images) && latestIssue.preview_images.length > 0 && (
        <section className="section-padding" style={{ background: '#0f0f11' }}>
          <div className="container-custom">
            <h2 className="section-title mb-8">Preview Pages</h2>
            <div className="preview-grid">
              {latestIssue.preview_images.map((preview, idx) => (
                <div key={idx} className="preview-image-container" data-testid={`preview-${idx}`}>
                  <img
                    src={`${API}/magazine/public/preview/${preview}`}
                    alt={`Preview ${idx + 1}`}
                    className="preview-image"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Previous Issues */}
      {issues.length > 0 && (
        <section className="section-padding">
          <div className="container-custom">
            <h2 className="section-title mb-8">Previous Issues</h2>
            <div className="issues-grid">
              {issues.map((issue) => (
                <Card
                  key={issue.issue_id}
                  className="issue-card"
                  data-testid={`issue-${issue.slug}`}
                >
                  <Link to={`/magazine/issue/${issue.slug}`}>
                    <img
                      src={`${API}/magazine/public/cover/${issue.cover_image}`}
                      alt={issue.title}
                      className="issue-card-cover"
                    />
                  </Link>
                  <div className="p-4">
                    <h3 className="text-xl font-bold text-white mb-2">{issue.title}</h3>
                    <p className="text-gray-400 text-sm mb-3">
                      {issue.issue_date
                        ? new Date(issue.issue_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long'
                          })
                        : ''}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-gold font-bold text-xl">
                        ${(typeof issue.price === 'number' ? issue.price : parseFloat(issue.price) || 0).toFixed(2)}
                      </span>
                      <Link to={`/magazine/issue/${issue.slug}`}>
                        <Button size="sm" variant="outline" className="text-white border-purple-500">
                          View Issue
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container-custom">
          <p className="footer-text">© 2026 Real Global Music. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default MagazineStorefront;